import { createContext, useContext, useMemo } from "react";
import type { IssueRow } from "#app/lib/data";
import type { IssueViewTab } from "./issue-view-types";
import type { Omittable } from "@salestrak/ui";
import { useDetailStackAnnounce } from "#app/components/detail-view/detail-stack";

export type OpenIssueOptions = Omittable<{
  initialTab?: IssueViewTab;
  focusNoteId?: string | null;
  /** Land the Activity tab on the first unread comment (the "New" marker at
   *  the top of the viewport, its thread expanded) instead of the bottom —
   *  the kanban card's lit comment action passes this. An explicit
   *  `focusNoteId` wins over it. */
  focusUnread?: boolean;
  // When set, the summary tab scrolls to and briefly highlights this custom
  // field row (e.g. opening a paragraph-mention notification from the inbox).
  focusCustomFieldId?: string | null;
  /** Focus the title input once the summary mounts — the menu Rename fallback
   *  for hosts with no inline rename surface. Re-opening an already-open
   *  issue with this set re-focuses the title. */
  focusTitle?: boolean;
  /** Override the stored desktop presentation for a contextual project open. */
  presentation?: "fullscreen-dialog";
  /** The surface the open came from, for `issue.view` analytics: a stable
   *  snake_case name such as "card", "list_row", "inbox", "search", "widget",
   *  "map" or "sidebar_favorites". */
  source?: string;
}>;

export type OpenCreateIssueOptions = Omittable<{
  workflowId?: string;
  stageId?: string;
  address?: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }>;
  name?: string;
  /** Seeds the draft's source (e.g. the Opportunities widget's origin source). The user can still change it. */
  issueSourceId?: string;
  /** Seeds the draft's Assigned Team — the team page the create opened from.
   *  Wins over the creator's own team preference; the user can still change it. */
  teamId?: string;
  /** Where the create flow was launched from — included in issue create_start/create_finish analytics. */
  source?: string;
  onCreated?: (issue: IssueRow) => void;
}>;

export interface IssueViewContextValue {
  openIssue: (issue: IssueRow, options?: OpenIssueOptions) => void;
  openIssueById: (issueId: string, options?: OpenIssueOptions) => void;
  openCreateIssue: (options: OpenCreateIssueOptions) => void;
  closeIssue: () => void;
  /** Close the view only if it shows this project (a delete from anywhere). */
  closeIssueIfOpen: (issueId: string) => void;
}

export const IssueViewContext = createContext<IssueViewContextValue | null>(null);

export const IssueViewBoundaryContext = createContext(false);

/**
 * The id of the project the issue view is showing, or null while it shows
 * nothing (a create draft counts as nothing). A narrow context of its own,
 * published by IssueViewProvider: a card marks itself "open" off this
 * (Asana's lit card beside the detail), and the whole board would otherwise
 * re-render on every tab switch inside the drawer if it read the full
 * dialog state.
 */
export const OpenIssueIdContext = createContext<string | null>(null);

/** The open project's id, or null. Safe outside the workspace layout. */
export function useOpenIssueId(): string | null {
  return useContext(OpenIssueIdContext);
}

function useScopedIssueView(
  issueView: IssueViewContextValue | null,
): IssueViewContextValue | null {
  const opensAsDialog = useContext(IssueViewBoundaryContext);
  const announce = useDetailStackAnnounce();

  // Every view open announces itself to the detail stack from HERE — the
  // caller's position decides whether it pushes or replaces. A create draft is
  // not a detail presentation and never announces.
  return useMemo(() => {
    if (!issueView) return null;
    const scoped = (options: OpenIssueOptions | undefined): OpenIssueOptions =>
      opensAsDialog
        ? { ...options, presentation: "fullscreen-dialog" }
        : { ...options };
    return {
      ...issueView,
      openIssue: (issue, options) => {
        announce("issue");
        issueView.openIssue(issue, scoped(options));
      },
      openIssueById: (issueId, options) => {
        announce("issue");
        issueView.openIssueById(issueId, scoped(options));
      },
    };
  }, [issueView, opensAsDialog, announce]);
}

export function useIssueView() {
  const issueView = useContext(IssueViewContext);
  const ctx = useScopedIssueView(issueView);
  if (!ctx) throw new Error("useIssueView must be used within IssueViewProvider");
  return ctx;
}

export function useIssueViewOptional() {
  const issueView = useContext(IssueViewContext);
  return useScopedIssueView(issueView);
}
